import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import * as moment from "moment";
import DeleteModal from "../../../Components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import { useProfile } from "../../../Components/Hooks/UserHooks";

import {
  getUsers as onGetUsers,
  addNewUser,
  updateUser,
  deleteUser,
} from "../../../slices/thunks";

const UserManagement = () => {
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [user, setUser] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile } = useProfile();
  const isAdmin = userProfile?.user?.groups?.includes("Admin");

  const selectUserState = createSelector(
    (state) => state.User,
    (user) => ({
      users: user.users || [],
      isUserSuccess: user.isUserSuccess,
      error: user.error,
    })
  );

  const { users, isUserSuccess, error } = useSelector(selectUserState);

  useEffect(() => {
    const handleResize = () => {
      setPageSize(window.innerWidth <= 480 ? 5 : 10);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(onGetUsers());
  }, [dispatch]);

  const handleCreateUserClick = () => {
    navigate("/table-users/add-user");
  };

  const onClickDelete = useCallback((user) => {
    setDeleteModal(true);
    setSelectedUser(user);
  }, []);

  const handleUserDetailsClick = (userData) => {
    navigate(`/table-users/user-details/${userData.id}`);
  };

  const toggle = useCallback(() => {
    setModal((prevModal) => !prevModal);
  }, []);

  const formatDate = (date) => {
    if (!date) return <span className="text-danger">No data</span>;
    return moment(new Date(date)).format("DD MMM Y");
  };

  const handleUserClick = useCallback(
    (userData) => {
      if (!userData) {
        toast.error("User data is not available. Please try again later.");
        return;
      }

      setUser({
        id: userData.id,
        username: userData.username,
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        is_superuser: userData.is_superuser || false,
        is_staff: userData.is_staff || false,
        is_active: userData.is_active || true,
        groups: userData.groups
          ? typeof userData.groups === "string"
            ? userData.groups.split(",")
            : userData.groups
          : [],
        date_joined: userData.date_joined,
        last_login: userData.last_login,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  const handleDeleteUser = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await dispatch(deleteUser(selectedUser?.id)).unwrap();
    } catch (err) {
      console.error("Failed to delete User");
    }
    setIsDeleting(false);
    setDeleteModal(false);
  });

  const columns = useMemo(
    () => [
      {
        header: "Username",
        accessorKey: "username",
        enableColumnFilter: false,
      },
      {
        header: "Date Joined",
        accessorKey: "date_joined",
        enableColumnFilter: false,
        cell: (cell) => formatDate(cell.getValue()),
      },
      {
        header: "Last Login",
        accessorKey: "last_login",
        enableColumnFilter: false,
        cell: (cell) => formatDate(cell.getValue()),
      },
      {
        header: "Superuser",
        accessorKey: "is_superuser",
        enableColumnFilter: false,
        cell: (cell) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        header: "Staff",
        accessorKey: "is_staff",
        enableColumnFilter: false,
        cell: (cell) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        header: "Groups",
        accessorKey: "groups",
        enableColumnFilter: false,
        cell: (cell) => {
          const groups = cell.getValue();
          if (!groups) return "No groups assigned";
          if (typeof groups === "string") return groups;
          if (Array.isArray(groups)) return groups.join(", ");
          return "No groups assigned";
        },
      },
      {
        header: "Action",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        size: 120,
        cell: (cellProps) => {
          return (
            <div className="d-flex gap-2">
              <button
                onClick={() => handleUserDetailsClick(cellProps.row.original)}
                className="btn btn-sm btn-soft-primary"
                title="View Details"
              >
                <i className="ri-eye-fill"></i>
              </button>

              {isAdmin && (
                <>
                  {/* <button
                    onClick={() => handleUserClick(cellProps.row.original)}
                    className="btn btn-sm btn-soft-warning"
                    title="Edit User"
                  >
                    <i className="ri-pencil-fill"></i>
                  </button> */}
                  <button
                    onClick={() => onClickDelete(cellProps.row.original)}
                    className="btn btn-sm btn-soft-danger"
                    title="Delete User"
                  >
                    <i className="ri-delete-bin-5-fill"></i>
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [isAdmin, handleUserClick]
  );

  return (
    <div className="page-content">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => {
          setDeleteModal(false);
        }}
        isDeleting={isDeleting}
        error={deleteError}
      />

      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="userList" className="shadow-sm">
              <CardHeader className="border-0 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">User Management</h5>
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleCreateUserClick}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Create
                      User
                    </button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {isUserSuccess ? (
                  users.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={users}
                      customPageSize={pageSize}
                      isGlobalFilter={true}
                      divClass="table-responsive"
                      tableClass="table table-hover table-centered align-middle"
                      theadClass="table-light text-muted"
                      SearchPlaceholder="Search users..."
                      initialState={{
                        columnVisibility: {},
                        sorting: [{ id: "username", desc: false }],
                      }}
                    />
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-md mx-auto mb-4">
                        <div className="avatar-title rounded-circle bg-light text-primary fs-24">
                          <i className="ri-user-search-line"></i>
                        </div>
                      </div>
                      <h5>No Users Found</h5>
                      {/* {isAdmin && (
                        <button 
                          className="btn btn-primary mt-2"
                          onClick={handleCreateUserClick}
                        >
                          Add New User
                        </button>
                      )} */}
                    </div>
                  )
                ) : (
                  <Loader error={error} />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserManagement;
